import imageCompression from 'browser-image-compression'
import { Field } from 'formik'
import { ChangeEvent, FC, useRef } from 'react'
import Svg from '../../svg'
import ErrorMessage from '../ErrorMessage'
import { StyleFormWrapper, StyleLabel } from '../style'
import {
  StyleAvatarImage,
  StyleAvatarWrapper,
  StyleButtonRow,
  StyleDragButton,
  StyleDragImageWrapper,
  StyleDragUploadWrapper,
  StyleIconWrapper,
  StyleUploadButton,
  StyleUploadButtonImgPreview,
  StyleUploadButtonWrapper,
  StyleUploadInput,
  StyleUploadInputFilename,
  StyleUploadInputWrapper,
  StyleVideoWrapper,
  StyleBannerUploadWrapper,
  StyleBannerUploadIconWrapper,
} from './style'

interface UploadProps {
  varient?: 'button' | 'drag' | 'input' | 'avatar' | 'video' | 'banner'
  name: string
  placeholder?: string
  onChange?: (value: any) => void
  label?: string
  styleLabel?: string
  styleWrapper?: string
  accept?: string
  size?: number
  disabled?: boolean
  icon?: any
}

const Upload: FC<UploadProps> = ({
  varient = 'button',
  name,
  label,
  placeholder,
  onChange,
  styleLabel = '',
  styleWrapper = '',
  size = 150,
  icon,
  ...restProps
}) => {
  const uploadInputRef = useRef<HTMLInputElement>(null)

  const handleChange = async ({ form, file }: any) => {
    const fileName = file.name
    const extension = fileName.split('.').pop()?.toLowerCase() || ''
    let compressedFile = file
    if (file?.type?.startsWith('image/')) {
      compressedFile = await imageCompression(file, {
        maxSizeMB: 3,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      })
    }
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const base64String = e.target?.result as string
      const [info, base64] = base64String.split(',')
      const fileData = { base64: base64.trim(), extension, info, fileName }
      if (!!onChange) {
        onChange?.(fileData)
      } else {
        form.setFieldValue(name, fileData)
      }
    }
    reader.readAsDataURL(compressedFile)
  }

  return (
    <Field name={name}>
      {({ form, field, meta }) => {
        const hasError = meta.touched && meta.error
        const fileValue = field.value
        const fileUrl = fileValue?.base64
          ? `${fileValue.info},${fileValue.base64}`
          : fileValue?.startsWith('https')
            ? fileValue
            : `${process.env.BUCKET_URL}/${fileValue}`
        const extension = fileValue?.extension || fileValue?.split('.')?.pop()
        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            {label && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
              </StyleLabel>
            )}

            {varient === 'video' && (
              <StyleDragUploadWrapper size={size}>
                {fileValue ? (
                  <StyleVideoWrapper
                    controls
                    onClick={(event: React.MouseEvent<HTMLVideoElement>) => {
                      event.preventDefault()
                      uploadInputRef.current?.click()
                    }}
                    src={fileUrl}
                  />
                ) : (
                  <StyleDragButton
                    type="button"
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    {placeholder}
                  </StyleDragButton>
                )}
              </StyleDragUploadWrapper>
            )}

            {varient === 'drag' && (
              <StyleDragUploadWrapper
                size={size}
                onClick={() => {
                  uploadInputRef.current?.click()
                }}
              >
                <StyleButtonRow>
                  {fileValue ? (
                    extension == 'pdf' ? (
                      <iframe src={fileUrl} height={size} />
                    ) : (
                      <StyleDragImageWrapper size={size} src={fileUrl} />
                    )
                  ) : (
                    <StyleDragButton type="button">
                      <Svg svg={icon || 'upload'} size={40} color={'text300'} />
                      {placeholder}
                    </StyleDragButton>
                  )}
                </StyleButtonRow>
              </StyleDragUploadWrapper>
            )}

            {varient == 'banner' && (
              <StyleBannerUploadWrapper
                size={size}
                onClick={(event: React.MouseEvent<HTMLVideoElement>) => {
                  event.preventDefault()
                  uploadInputRef.current?.click()
                }}
              >
                {!!fileValue ? (
                  <StyleDragImageWrapper size={size} src={fileUrl} />
                ) : (
                  <StyleBannerUploadIconWrapper>
                    <Svg svg="edit" color="secondary" size={16} />
                  </StyleBannerUploadIconWrapper>
                )}
              </StyleBannerUploadWrapper>
            )}

            {varient === 'avatar' && (
              <StyleAvatarWrapper
                size={size}
                onClick={() => uploadInputRef.current?.click()}
                hasError={hasError}
              >
                {fileValue ? (
                  <StyleAvatarImage src={fileUrl} />
                ) : (
                  <Svg svg="uploadUser" color="text600" size={60} />
                )}
                <StyleIconWrapper>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_1166_20481)">
                      <path
                        d="M4.2526 3.47852C3.60159 3.48047 3.24131 3.49604 2.94526 3.6203C2.53058 3.79436 2.19287 4.11586 1.99559 4.52443C1.83456 4.85792 1.80821 5.28518 1.75551 6.13969L1.67293 7.47873C1.54188 9.60375 1.47635 10.6663 2.0999 11.3389C2.72346 12.0116 3.77395 12.0116 5.87495 12.0116H7.96359C10.0646 12.0116 11.1151 12.0116 11.7386 11.3389C12.3622 10.6663 12.2967 9.60375 12.1656 7.47873L12.083 6.13969C12.0303 5.28518 12.004 4.85792 11.8429 4.52443C11.6457 4.11586 11.308 3.79436 10.8933 3.6203C10.5972 3.49604 10.2369 3.48047 9.58594 3.47852"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M9.58529 4.01237L9.11286 2.83127C8.90902 2.32168 8.73163 1.74362 8.20747 1.48413C7.9279 1.3457 7.59147 1.3457 6.91862 1.3457C6.24577 1.3457 5.90934 1.3457 5.62974 1.48413C5.10561 1.74362 4.92823 2.32168 4.7244 2.83127L4.25195 4.01237"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.78607 7.74557C8.78607 8.77651 7.95033 9.61224 6.9194 9.61224C5.88847 9.61224 5.05273 8.77651 5.05273 7.74557C5.05273 6.71464 5.88847 5.87891 6.9194 5.87891C7.95033 5.87891 8.78607 6.71464 8.78607 7.74557Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                      />
                      <path
                        d="M6.91992 3.47852H6.92472"
                        stroke="#28A8FF"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1166_20481">
                        <rect
                          width="12.8"
                          height="12.8"
                          fill="white"
                          transform="translate(0.519531 0.279297)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </StyleIconWrapper>
              </StyleAvatarWrapper>
            )}

            {varient === 'button' && (
              <StyleUploadButtonWrapper
                hasError={hasError}
                onClick={() => uploadInputRef.current?.click()}
              >
                <StyleUploadButton>
                  {placeholder || 'Browse File'}
                </StyleUploadButton>
                {fileValue && <StyleUploadButtonImgPreview src={fileUrl} />}
              </StyleUploadButtonWrapper>
            )}

            {varient === 'input' && (
              <StyleUploadInputWrapper
                hasError={hasError}
                onClick={() => uploadInputRef.current?.click()}
              >
                <StyleUploadInputFilename>
                  {fileValue?.fileName || placeholder}
                </StyleUploadInputFilename>
                <Svg svg="upload" color="primaryTextColor" />
              </StyleUploadInputWrapper>
            )}

            {hasError && (
              <ErrorMessage
                message={meta.error?.base64 || meta.error?.extension}
              />
            )}

            <StyleUploadInput
              type="file"
              ref={uploadInputRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleChange({ form, file: event.target.files?.[0] })
                event.target.value = ''
              }}
              {...restProps}
            />
          </StyleFormWrapper>
        )
      }}
    </Field>
  )
}

export default Upload
